/** @typedef {import('headlight-configurable-application').Pict} Pict */
const PictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;

/*
	Tier-2.5 accessibility helper for the Color input type.

	`readonly` does nothing on `<input type="color">`, so the Read (locked) theme can't use
	the text-input readonly swap. Instead the Read template renders a static display node
	(`DISPLAY-FOR-{RawHTMLID}`) and this extension paints a color swatch plus the hex value
	text into it — readable and announced, instead of a `disabled` color control that screen
	readers skip and that gives no textual value.

	Acts only when the display node is present (Read theme); a no-op in Edit/Print, where
	`super` still drives the core informary binding of the live control. Per the repo
	convention we build node content with createElement/append, not HTML strings.
*/
class CustomInputHandler extends PictSectionInputExtension
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
	}

	/**
	 * Returns the HTML selector for the Read-theme static display node.
	 * @param {string} pInputHTMLID - The raw HTML ID of the input.
	 * @return {string} - The selector for the display node.
	 */
	getDisplayHTMLID(pInputHTMLID)
	{
		return `#DISPLAY-FOR-${pInputHTMLID}`;
	}

	/**
	 * Returns the HTML selector for the Read-theme tabular static display node.
	 * @param {string} pInputHTMLID - The raw HTML ID of the input.
	 * @param {number} pRowIndex - The tabular row index.
	 * @return {string} - The selector for the tabular display node.
	 */
	getTabularDisplayHTMLID(pInputHTMLID, pRowIndex)
	{
		return `#DISPLAY-TABULAR-FOR-${pInputHTMLID}-${pRowIndex}`;
	}

	/**
	 * Normalizes a marshalled value into a `#rrggbb` (or `#rgb`) hex string, or null if it
	 * doesn't look like a hex color. Used both as the swatch background and the visible text.
	 * @param {any} pValue - The value to normalize.
	 * @return {string|null} - The normalized hex string, or null.
	 */
	normalizeHex(pValue)
	{
		if (typeof pValue !== 'string')
		{
			return null;
		}
		const tmpTrimmed = pValue.trim();
		return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(tmpTrimmed) ? tmpTrimmed.toLowerCase() : null;
	}

	/**
	 * Paints the swatch + hex text into the display node, if present.
	 * @param {string} pDisplaySelector - The selector for the display node.
	 * @param {any} pValue - The marshalled value.
	 * @return {void}
	 */
	renderColorDisplay(pDisplaySelector, pValue)
	{
		const tmpElements = this.pict.ContentAssignment.getElement(pDisplaySelector);
		if (!tmpElements || tmpElements.length < 1)
		{
			return;
		}
		const tmpHex = this.normalizeHex(pValue);

		const tmpContainer = document.createElement('span');
		tmpContainer.className = 'color-display';

		const tmpSwatch = document.createElement('span');
		tmpSwatch.className = 'color-swatch';
		// Swatch is decorative; the adjacent hex text carries the value for assistive tech.
		tmpSwatch.setAttribute('aria-hidden', 'true');
		if (tmpHex)
		{
			tmpSwatch.style.backgroundColor = tmpHex;
		}
		tmpContainer.appendChild(tmpSwatch);

		const tmpText = document.createElement('span');
		tmpText.textContent = tmpHex || (typeof pValue === 'string' && pValue.trim() ? pValue.trim() : 'None');
		tmpContainer.appendChild(tmpText);

		for (let i = 0; i < tmpElements.length; i++)
		{
			const tmpTarget = tmpElements[i];
			// Clear via a removeChild loop rather than replaceChildren(), which isn't available
			// on the older browsers in this bundle's "since 2018" browserslist target.
			while (tmpTarget.firstChild)
			{
				tmpTarget.removeChild(tmpTarget.firstChild);
			}
			tmpTarget.appendChild(tmpContainer.cloneNode(true));
		}
	}

	/**
	 * Marshals the color value into the Read-theme display node (no-op outside Read).
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to marshal.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {any} - The result of super.onDataMarshalToForm.
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this.renderColorDisplay(this.getDisplayHTMLID(pInput.Macro.RawHTMLID), pValue);
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Marshals the color value into the Read-theme tabular display node (no-op outside Read).
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to marshal.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The row index.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {any} - The result of super.onDataMarshalToFormTabular.
	 */
	onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this.renderColorDisplay(this.getTabularDisplayHTMLID(pInput.Macro.RawHTMLID, pRowIndex), pValue);
		return super.onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}
}

module.exports = CustomInputHandler;
